import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import Breadcrumbs from '../../components/breadcrumbs'
import Footer from '../../components/footer'
import DarkNav from '../../components/dark-nav'
import { getCanonicalUrl } from '../../utils/canonical'
import './gohighlevel.css'

const GoHighLevelIntegration = () => {
  const [showScrollTop, setShowScrollTop] = useState(false)
  const [openFaq, setOpenFaq] = useState(null)

  useEffect(() => {
    if (typeof AOS !== 'undefined') {
      AOS.init({ duration: 800, easing: 'ease-out', once: false, offset: 100, mirror: true })
    }
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300)
      if (typeof AOS !== 'undefined') AOS.refresh()
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Integrations' },
    { label: 'GoHighLevel' }
  ]

  const features = [
    {
      icon: '⚡',
      title: 'Workflow Automation',
      desc: 'Trigger SMS from any workflow step — contact created, form submitted, appointment booked, deal stage changed, and more. Fully automated, no manual action needed.'
    },
    {
      icon: '💬',
      title: 'Two-Way Conversations',
      desc: 'Receive customer replies directly back into your HighLevel account. Handle responses and keep conversations in one place without switching tools.'
    },
    {
      icon: '🏷️',
      title: 'Branded Sender ID',
      desc: 'Send from your business name instead of a random number. Every message arrives looking like it came from your brand — building trust at every touchpoint.'
    },
    {
      icon: '📢',
      title: 'Bulk Campaigns',
      desc: 'Send SMS broadcasts to your full contact list or a targeted segment — promotions, announcements, appointment reminders, and re-engagement campaigns.'
    },
    {
      icon: '🎯',
      title: 'Custom Field Personalisation',
      desc: 'Use HighLevel custom values to personalise every message with the contact\'s name, business details, or any CRM field — at scale.'
    },
    {
      icon: '🔐',
      title: 'OAuth Setup',
      desc: 'Connect your DataFlows account in minutes with secure OAuth. No coding required. Your workflows are SMS-ready immediately after setup.'
    },
  ]

  const useCases = [
    {
      icon: '📅',
      title: 'Appointment Reminders',
      desc: 'Trigger SMS reminders automatically when an appointment is booked or approaching — reduce no-shows and keep your pipeline moving.'
    },
    {
      icon: '🤝',
      title: 'Lead Nurture Sequences',
      desc: 'Add SMS touchpoints to your nurture workflows. Reach leads at the right moment with personalised messages based on their stage in the funnel.'
    },
    {
      icon: '🔁',
      title: 'Deal Stage Notifications',
      desc: 'Send an SMS when a contact moves between pipeline stages — keep prospects engaged and your team informed automatically.'
    },
    {
      icon: '📋',
      title: 'Form Submission Follow-Up',
      desc: 'Instantly follow up when a contact submits a form. A well-timed SMS response dramatically increases conversion from enquiry to booked appointment.'
    },
  ]

  const faqItems = [
    {
      q: 'How do I connect DataFlows to GoHighLevel?',
      a: 'Install the DataFlows SMS integration from the GoHighLevel Marketplace. Connect your DataFlows account using OAuth — no coding required. Once connected, DataFlows SMS actions are available in your workflows immediately.'
    },
    {
      q: 'Can I send personalised SMS from GoHighLevel workflows?',
      a: 'Yes. Use HighLevel custom values (such as {{contact.first_name}}, {{contact.business_name}}, or any custom field) directly inside your DataFlows SMS message templates. Every message is personalised at send time.'
    },
    {
      q: 'Does DataFlows support two-way SMS inside GoHighLevel?',
      a: 'Yes. Replies from your contacts are delivered back into your HighLevel account so you can handle responses without leaving your CRM. Conversations are tracked per contact.'
    },
    {
      q: 'Is this integration compliant with Australian SMS law?',
      a: 'Yes. DataFlows is an ACMA-registered Originating Telco, listed on the official ACMA SMS Sender ID Register. All messaging is compliant with the Australian Spam Act 2003. Sender ID registration and Australian compliance are handled automatically — you don\'t need to do anything separately.'
    },
    {
      q: 'Do I need a DataFlows account to use this integration?',
      a: 'Yes. You need an active DataFlows account. Pricing is pay-as-you-go from $35 AUD, with no monthly fees and no lock-in contracts. Create your account at sms.dataflows.com.au and then install the integration from the HighLevel Marketplace.'
    },
    {
      q: 'Can I use a branded sender ID with GoHighLevel?',
      a: 'Yes. DataFlows supports alphanumeric sender IDs, so your messages arrive displaying your business name instead of a phone number. Sender IDs are registered with ACMA through DataFlows on your behalf.'
    },
  ]

  return (
    <div className="ghl-container">
      <Helmet>
        <title>GoHighLevel SMS Integration — DataFlows SMS for HighLevel CRM</title>
        <meta name="description" content="Send automated SMS from GoHighLevel workflows using DataFlows. Trigger messages from any workflow step, run bulk campaigns, and manage two-way conversations — ACMA compliant Australian SMS." />
        <meta name="keywords" content="GoHighLevel SMS, HighLevel SMS integration, GoHighLevel SMS Australia, CRM SMS automation, HighLevel workflow SMS, GoHighLevel DataFlows, Australian CRM SMS, HighLevel bulk SMS, GoHighLevel two-way SMS" />
        <meta property="og:title" content="GoHighLevel SMS Integration — DataFlows SMS for HighLevel CRM" />
        <meta property="og:description" content="Trigger SMS from any GoHighLevel workflow step. Branded sender ID, two-way conversations, bulk campaigns — ACMA-compliant Australian SMS built for agencies and businesses." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={getCanonicalUrl('/integrations/gohighlevel')} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "DataFlows SMS for GoHighLevel",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "GoHighLevel",
          "url": "https://marketplace.gohighlevel.com/integration/6a15b8b02a575d4e9868c0f3/versions/6a15b8b02a575d4e9868c0f3",
          "description": "Send automated SMS from GoHighLevel workflows. Trigger messages from any workflow step, run bulk campaigns, manage two-way conversations. ACMA-compliant Australian SMS.",
          "offers": { "@type": "Offer", "price": "35.00", "priceCurrency": "AUD" },
          "author": { "@type": "Organization", "name": "DataFlows SMS", "url": "https://dataflows.com.au" }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://dataflows.com.au/" },
            { "@type": "ListItem", "position": 2, "name": "Integrations", "item": "https://dataflows.com.au/integrations" },
            { "@type": "ListItem", "position": 3, "name": "GoHighLevel", "item": "https://dataflows.com.au/integrations/gohighlevel" }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      <DarkNav />

      <div className="ghl-breadcrumbs">
        <div className="ghl-inner">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>

      {/* Hero */}
      <section className="ghl-hero" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-hero-badge">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 14.5v-9l6 4.5-6 4.5z"/></svg>
            GoHighLevel Marketplace
          </div>
          <h1 className="ghl-hero-title">DataFlows SMS for GoHighLevel</h1>
          <p className="ghl-hero-subtitle">
            Bring powerful SMS messaging directly into your HighLevel workflows. Send personalised messages, run broadcast campaigns, and manage two-way conversations — all without leaving your CRM.
          </p>
          <div className="ghl-hero-actions">
            <a
              href="https://marketplace.gohighlevel.com/integration/6a15b8b02a575d4e9868c0f3/versions/6a15b8b02a575d4e9868c0f3"
              target="_blank"
              rel="noreferrer noopener"
              className="ghl-btn-primary"
            >
              Install on GoHighLevel
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd"/></svg>
            </a>
            <a href="https://sms.dataflows.com.au/register" target="_blank" rel="noreferrer noopener" className="ghl-btn-outline">
              Create DataFlows Account
            </a>
          </div>
          <div className="ghl-hero-trust">
            <span>✓ ACMA Registered Originating Telco</span>
            <span>✓ Spam Act 2003 Compliant</span>
            <span>✓ OAuth — No Coding Required</span>
            <span>✓ Pay-as-you-go from $35 AUD</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="ghl-section" id="features" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-section-header">
            <span className="ghl-label">Features</span>
            <h2>Everything You Need for CRM-Powered SMS</h2>
            <p>DataFlows adds a full SMS channel to your GoHighLevel account — automation, two-way messaging, branded sender ID, and bulk campaigns in one integration.</p>
          </div>
          <div className="ghl-features-grid">
            {features.map((f, i) => (
              <div key={i} className="ghl-feature-card" data-aos="fade-up" data-aos-delay={i * 60}>
                <span className="ghl-feature-icon">{f.icon}</span>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two-Way Conversations */}
      <section className="ghl-section ghl-section--dark" id="conversations" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-two-col">
            <div className="ghl-two-col-text">
              <span className="ghl-label">Two-Way SMS</span>
              <h2>Replies Come Back Into HighLevel</h2>
              <p>When a contact replies to your SMS, the response is delivered straight back into your GoHighLevel account. No separate inbox. No missed messages. All conversations in one place.</p>
              <ul className="ghl-checklist">
                <li>Inbound replies visible in HighLevel conversations</li>
                <li>Respond without leaving your CRM</li>
                <li>Full message history per contact</li>
                <li>Works with workflow-triggered messages and manual sends</li>
              </ul>
            </div>
            <div className="ghl-conversation-preview">
              <div className="ghl-conversation-thread">
                <div className="ghl-msg ghl-msg--out">Hi Sarah, just a reminder your appointment is tomorrow at 10am. Reply YES to confirm or STOP to opt out. — Bright Dental</div>
                <div className="ghl-msg ghl-msg--in">YES, confirmed. See you then!</div>
                <div className="ghl-msg ghl-msg--out">Great, we'll see you tomorrow Sarah. Parking is available on site. 😊</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How to Set Up */}
      <section className="ghl-section" id="setup" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-section-header">
            <span className="ghl-label">Setup</span>
            <h2>Up and Running in Minutes</h2>
            <p>No developers, no API keys to manage manually. Just OAuth and you're done.</p>
          </div>
          <div className="ghl-steps">
            <div className="ghl-step" data-aos="fade-up" data-aos-delay="0">
              <div className="ghl-step-num">1</div>
              <h3>Create a DataFlows Account</h3>
              <p>Sign up at sms.dataflows.com.au. Pay-as-you-go from $35 AUD, with no monthly fees and no lock-in contract.</p>
            </div>
            <div className="ghl-step" data-aos="fade-up" data-aos-delay="80">
              <div className="ghl-step-num">2</div>
              <h3>Install from HighLevel Marketplace</h3>
              <p>Find DataFlows SMS in the GoHighLevel Marketplace and click Install. Authorise with OAuth — no code needed.</p>
            </div>
            <div className="ghl-step" data-aos="fade-up" data-aos-delay="160">
              <div className="ghl-step-num">3</div>
              <h3>Add SMS to Your Workflows</h3>
              <p>Open any workflow in HighLevel and add the DataFlows SMS action. Personalise your message with custom values and activate.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="ghl-section ghl-section--dark" id="use-cases" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-section-header">
            <span className="ghl-label">Use Cases</span>
            <h2>Built for Agencies &amp; Australian Businesses</h2>
            <p>Whether you're running client campaigns or managing your own pipeline, DataFlows SMS fits naturally into any HighLevel workflow.</p>
          </div>
          <div className="ghl-usecases-grid">
            {useCases.map((u, i) => (
              <div key={i} className="ghl-usecase-card" data-aos="fade-up" data-aos-delay={i * 60}>
                <span className="ghl-usecase-icon">{u.icon}</span>
                <h3>{u.title}</h3>
                <p>{u.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Compliance */}
      <section className="ghl-section" id="compliance" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-two-col">
            <div className="ghl-two-col-text">
              <span className="ghl-label">Compliance</span>
              <h2>Australian SMS Compliance, Handled</h2>
              <p>DataFlows is an ACMA-registered Originating Telco, listed on the official ACMA SMS Sender ID Register. Sender ID registration and Australian compliance are handled for you automatically.</p>
              <ul className="ghl-checklist">
                <li>ACMA-registered Originating Telco</li>
                <li>Compliant with the Spam Act 2003</li>
                <li>STOP keyword always active — cannot be disabled</li>
                <li>Sender ID registered with ACMA on your behalf</li>
                <li>Opt-outs tracked and honoured automatically</li>
              </ul>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div className="ghl-feature-card">
                <h3 style={{ color: '#f97316', marginBottom: '8px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Who is this for?</h3>
                <p>Marketing agencies and Australian businesses using GoHighLevel for lead management, client communication, and customer engagement who want to add SMS as a channel.</p>
              </div>
              <div className="ghl-feature-card">
                <h3 style={{ color: '#f97316', marginBottom: '8px', fontSize: '14px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Requirements</h3>
                <p>An active DataFlows account (pay-as-you-go from $35 AUD, no monthly fees) and an active GoHighLevel account. No technical setup required beyond OAuth authorisation.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="ghl-section ghl-section--dark" id="faq" data-aos="fade-up">
        <div className="ghl-inner">
          <div className="ghl-section-header">
            <span className="ghl-label">FAQ</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="ghl-faq">
            {faqItems.map((item, i) => (
              <div key={i} className={`ghl-faq-item${openFaq === i ? ' open' : ''}`}>
                <button className="ghl-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  {item.q}
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 9l6 6 6-6"/></svg>
                </button>
                <p className={`ghl-faq-a${openFaq === i ? '' : ' ghl-faq-a--hidden'}`}>{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="ghl-section" data-aos="fade-up">
        <div className="ghl-inner">
          <h2 className="ghl-related-title">Related Integrations</h2>
          <div className="ghl-related-links">
            <Link to="/integrations/zapier" className="ghl-related-link">Zapier SMS</Link>
            <Link to="/integrations/power-automate" className="ghl-related-link">Power Automate SMS</Link>
            <Link to="/integrations/shopify" className="ghl-related-link">Shopify SMS</Link>
            <Link to="/integrations/wordpress" className="ghl-related-link">WordPress / WooCommerce SMS</Link>
            <Link to="/integrations/microsoft-teams" className="ghl-related-link">Microsoft Teams SMS</Link>
            <span className="ghl-related-link">GoHighLevel SMS Australia</span>
            <span className="ghl-related-link">CRM SMS Automation</span>
            <span className="ghl-related-link">HighLevel Workflow SMS</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="ghl-cta" data-aos="fade-up">
        <div className="ghl-inner">
          <h2>Ready to Add SMS to Your HighLevel Workflows?</h2>
          <p>Install DataFlows SMS from the GoHighLevel Marketplace and start sending in minutes.</p>
          <div className="ghl-cta-buttons">
            <a
              href="https://marketplace.gohighlevel.com/integration/6a15b8b02a575d4e9868c0f3/versions/6a15b8b02a575d4e9868c0f3"
              target="_blank"
              rel="noreferrer noopener"
              className="ghl-btn-primary"
            >
              Install on GoHighLevel Marketplace
            </a>
            <Link to="/contact-us" className="ghl-btn-outline">Talk to us</Link>
          </div>
        </div>
      </section>

      <Footer />

      {showScrollTop && (
        <div className="scroll-to-top show" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
          <svg viewBox="0 0 24 24" fill="none"><path d="M12 5L19 12H16V19H8V12H5L12 5Z" fill="currentColor"/></svg>
        </div>
      )}
    </div>
  )
}

export default GoHighLevelIntegration
